import type { ReactNode } from 'react'
import { Banner, Button, Card, FateTokenRow, SectionLabel } from '../../../design/components'
import type { CoreSection, FateSection } from '../types'
import type { CharacterEditing } from '../useCharacterEditing'
import { SHEET_SECTION_ID } from './sheetSections'
import styles from './sections.module.css'

/**
 * Fate points and refresh (spec §5.1, §5.2 "Fate-point economy").
 *
 * The card is two readings side by side. Left is the pool — one number, because
 * it changes every few minutes at the table and is nothing but a count. Right is
 * the refresh budget, which is what a player actually needs when they buy a power
 * or start a session: how much refresh the powers already eat, and how much is
 * left. Those two used to be the smallest text on the card; here they are the
 * other half of it.
 *
 * Spending and earning a point is optimistic — it is a single number with an
 * obvious inverse. The refresh figures are *not* touched optimistically:
 * `refreshUsed` sums the abilities' costs server-side, so a guess here would be
 * inventing a number.
 */
export function FateCard({
  fate,
  core,
  editing,
}: {
  fate: FateSection | undefined
  /** Carries `totalRefresh` — the character's refresh, which the budget column edits. */
  core?: CoreSection | undefined
  editing?: CharacterEditing | undefined
}) {
  if (fate === undefined) return null

  const canEdit = editing?.canEdit === true
  const { fatePoints, refreshLevel, refreshUsed, refreshAvailable } = fate

  // `refreshAvailable` goes negative on an over-limit character. Nothing below
  // draws or awards a negative number of points, so it is floored once here.
  const available = Math.max(refreshAvailable, 0)

  return (
    <Card id={SHEET_SECTION_ID.fate} variant="fate" label="Fate &amp; refresh">
      <div className={styles.fateTop}>
        <div className={styles.fatePool}>
          <SectionLabel tone="muted" micro>
            Fate points
          </SectionLabel>

          <span className={styles.fatePoolNumber}>{fatePoints}</span>

          {canEdit && editing !== undefined && (
            <div className={styles.fatePoolSteppers}>
              <button
                type="button"
                className={styles.stepper}
                aria-label="Spend a fate point"
                disabled={fatePoints <= 0}
                onClick={() => editing.fate.mutate({ fatePoints: fatePoints - 1 })}
              >
                −
              </button>
              {/* No upper bound: compels and concessions can bank a pool well past
                  the refresh rate, and the sheet is not the place that argues. */}
              <button
                type="button"
                className={styles.stepper}
                aria-label="Earn a fate point"
                onClick={() => editing.fate.mutate({ fatePoints: fatePoints + 1 })}
              >
                +
              </button>
            </div>
          )}

          <FateTokenRow
            current={fatePoints}
            max={available}
            showCount={false}
            className={styles.fateCoins}
          />
        </div>

        <div className={styles.refreshLedger}>
          <SectionLabel tone="muted" micro>
            Refresh budget
          </SectionLabel>

          <div
            className={styles.refreshMeter}
            role="img"
            aria-label={`${refreshUsed} of ${refreshLevel} refresh spent on powers, ${refreshAvailable} available`}
          >
            <span
              className={fate.isOverLimit ? styles.refreshMeterOver : styles.refreshMeterUsed}
              style={{ flexGrow: refreshUsed }}
            />
            <span className={styles.refreshMeterFree} style={{ flexGrow: available }} />
          </div>

          <div className={styles.refreshStats}>
            <Stat
              label="Available"
              value={refreshAvailable}
              tone={fate.isOverLimit ? 'warn' : 'good'}
            />
            <Stat label="Used" value={refreshUsed} />
            <Stat
              label="Refresh"
              value={refreshLevel}
              /* The character's own refresh, edited where it is read. It used to be a
                 separate "base" figure below the card, because the number above it was
                 base plus a character level — an addition the system does not have. */
              control={
                canEdit && editing !== undefined && core !== undefined ? (
                  <>
                    <button
                      type="button"
                      className={styles.stepperSm}
                      aria-label="Lower refresh"
                      disabled={editing.totalRefresh.isPending || core.totalRefresh <= 0}
                      onClick={() =>
                        editing.totalRefresh.mutate({ totalRefresh: core.totalRefresh - 1 })
                      }
                    >
                      −
                    </button>
                    <button
                      type="button"
                      className={styles.stepperSm}
                      aria-label="Raise refresh"
                      disabled={editing.totalRefresh.isPending}
                      onClick={() =>
                        editing.totalRefresh.mutate({ totalRefresh: core.totalRefresh + 1 })
                      }
                    >
                      +
                    </button>
                  </>
                ) : undefined
              }
            />
          </div>
        </div>
      </div>

      {canEdit && editing !== undefined && (
        <div className={styles.sessionRow}>
          <Button
            onClick={() => editing.fate.mutate({ fatePoints: fatePoints + available })}
            disabled={available <= 0 || editing.fate.isPending}
          >
            Start session
            <span className={styles.sessionDelta}>+{available}</span>
          </Button>
        </div>
      )}

      {fate.isOverLimit && (
        <div className={styles.pyramidWarning}>
          <Banner tone="warn">
            This character spends more refresh than they have. Drop a power or raise refresh.
          </Banner>
        </div>
      )}
    </Card>
  )
}

function Stat({
  label,
  value,
  tone = 'plain',
  control,
}: {
  label: string
  value: number
  tone?: 'plain' | 'good' | 'warn'
  control?: ReactNode
}) {
  const valueClass =
    tone === 'warn'
      ? styles.refreshStatValueWarn
      : tone === 'good'
        ? styles.refreshStatValueGood
        : styles.refreshStatValue

  return (
    <div className={styles.refreshStat}>
      <SectionLabel tone="muted" micro>
        {label}
      </SectionLabel>
      <span className={styles.refreshStatFigure}>
        {control}
        <span className={valueClass}>{value}</span>
      </span>
    </div>
  )
}
